import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { ListPage } from "../components/ListPage";
import { Badge } from "../components/ui/badge";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon, Table2 } from "lucide-react";

const TYPE_COLORS = {
  Plant: "#1B2A4A", "DC/Warehouse": "#0891B2", "HQ/Office": "#F5A623", "Co-Packer Site": "#7C3AED",
};
const TYPE_BADGES = {
  Plant: "bg-slate-100 text-slate-700", "DC/Warehouse": "bg-cyan-50 text-cyan-700",
  "HQ/Office": "bg-amber-50 text-amber-700", "Co-Packer Site": "bg-purple-50 text-purple-700",
};

const pin = (color) => L.divIcon({
  className: "",
  html: `<svg width="26" height="34" viewBox="0 0 26 34"><path d="M13 0C5.8 0 0 5.8 0 13c0 9.8 13 21 13 21s13-11.2 13-21C26 5.8 20.2 0 13 0z" fill="${color}"/><circle cx="13" cy="13" r="5" fill="white"/></svg>`,
  iconSize: [26, 34], iconAnchor: [13, 34], popupAnchor: [0, -30],
});

export default function Locations() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [view, setView] = useState("table");
  const [locs, setLocs] = useState([]);

  useEffect(() => {
    api.get("/locations?limit=1000").then((r) => setLocs(r.data)).catch(() => {});
  }, []);

  const columns = [
    { key: "name", label: t("name"), render: (r) => <span className="font-medium text-[#1B2A4A]">{r.name}</span> },
    { key: "account_name", label: t("account"), render: (r) => (
      <button className="text-blue-600 hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${r.account_id}`); }}>{r.account_name}</button>) },
    { key: "site_type", label: t("site_type"), render: (r) => <Badge className={`${TYPE_BADGES[r.site_type] || ""} hover:bg-inherit`}>{r.site_type}</Badge> },
    { key: "city", label: t("city") },
    { key: "country", label: t("country") },
    { key: "region", label: t("region") },
    { key: "currency", label: "€/$", render: (r) => <span className="font-mono text-xs">{r.currency}</span> },
    { key: "erp_customer_number", label: t("erp_number"), render: (r) => <span className="font-mono text-xs">{r.erp_customer_number}</span> },
    { key: "account_manager_name", label: t("account_manager") },
  ];

  const toolbar = () => (
    <div className="flex rounded-md border border-gray-200 overflow-hidden">
      <button data-testid="locations-view-table" onClick={() => setView("table")}
        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "table" ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600"}`}>
        <Table2 className="h-3.5 w-3.5" />{t("table")}
      </button>
      <button data-testid="locations-view-map" onClick={() => setView("map")}
        className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${view === "map" ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600"}`}>
        <MapIcon className="h-3.5 w-3.5" />{t("map_view")}
      </button>
    </div>
  );

  if (view === "map") {
    const withGps = locs.filter((l) => l.lat && l.lng);
    return (
      <div className="space-y-4" data-testid="locations-page">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("locations")}</h1>
          {toolbar()}
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.entries(TYPE_COLORS).map(([k, c]) => (
            <span key={k} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: c }} />{k}</span>
          ))}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" style={{ height: "calc(100vh - 240px)" }} data-testid="locations-map">
          <MapContainer center={[45, 5]} zoom={3} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            {withGps.map((l) => (
              <Marker key={l.id} position={[l.lat, l.lng]} icon={pin(TYPE_COLORS[l.site_type] || "#1B2A4A")}>
                <Popup>
                  <p className="font-semibold">{l.name}</p>
                  <p className="text-xs">{l.account_name}</p>
                  <p className="text-xs text-gray-500">{l.site_type} · {l.city}, {l.country}</p>
                  <p className="text-xs text-gray-400">{l.erp_customer_number}</p>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    );
  }

  return <ListPage module="locations" titleKey="locations" columns={columns} extraToolbar={toolbar} />;
}
